import { Component, DestroyRef, ElementRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { CasinoMiniGamesConfig } from '@frontend/sports/common/client-config-data-access';
import { DispatcherService } from '@frontend/sports/common/core/utils/dispatcher';
import { TrustPipe } from '@frontend/sports/common/core/utils/dom';
import { ScrollContainerService } from '@frontend/sports/common/core/utils/scroll-container';
import { RedirectHelperService } from '@frontend/sports/navigation/core/feature/redirect';
import { UserService } from '@frontend/sports/user/feature';
import { DsLoadingSpinner } from '@frontend/ui/loading-spinner';
import { BalancePropertiesService } from '@frontend/vanilla/features/balance-properties';
import { KycStatus, KycStatusService } from '@frontend/vanilla/shared/kyc';

import { BetColumnType } from '../bet-column/bet-column-type';
import { GeolocationCheckService } from '../geo-location/geo-location-check.service';
import {
    CasinoWindowEvent,
    LobbyLoadedEvent,
    PingStatusGameOpenEvent,
    RecentCasinoGameCloseEvent,
    RecentCasinoGameOpenEvent,
    ShowLoginEvent,
    WindowScrollStartEvent,
} from './casino.event';
import { MiniGamesTrackingService } from './minigames-tracking.service';
import { MiniGamesService } from './minigames.service';
import { MiniGamesWidgetPostSessionKeyEvent } from './sports.event';

export interface Size {
    target?: string;
    class: MiniGamesSize;
}

export enum MiniGamesSize {
    Small = 'small',
    Medium = 'medium',
    Large = 'large',
    ExtraLarge = 'xl',
    SmallOpened = 'small-opened',
    MediumOpened = 'medium-opened',
    LargeOpened = 'large-opened',
    ExtraLargeOpened = 'xl-opened',
}

@Component({
    selector: 'ms-minigames',
    templateUrl: './minigames.component.html',
    styleUrl: './minigames.component.scss',
    imports: [TrustPipe, DsLoadingSpinner],
})
export class MinigamesComponent implements OnDestroy, OnInit {
    private shouldLogin = false;
    private currentType = BetColumnType.Betslip;

    isKycVerified = false;
    minigamesUrl = '';
    minigamesOpen = false;
    loaderVisible = false;

    @ViewChild('casinoIframe', { static: true }) casinoIframe: ElementRef;

    private readonly destroyRef = inject(DestroyRef);
    private readonly miniGamesService = inject(MiniGamesService);
    private readonly redirectHelper = inject(RedirectHelperService);
    private readonly userService = inject(UserService);
    private readonly balanceService = inject(BalancePropertiesService);
    private readonly dispatcher = inject(DispatcherService);
    private readonly miniGamesTrackingService = inject(MiniGamesTrackingService);
    private readonly scrollContainer = inject(ScrollContainerService);
    private readonly geolocationCheckService = inject(GeolocationCheckService);
    private readonly casinoMiniGamesConfig = inject(CasinoMiniGamesConfig);
    private readonly kycStatusService = inject(KycStatusService);

    constructor() {
        this.minigamesUrl = this.miniGamesService.getUrl();
        if (this.userService.isAuthenticated) {
            this.geolocationCheckService.checkGeolocation();
        }
        this.minigamesOpen = this.isGameOpened();

        this.miniGamesService.events.pipe(takeUntilDestroyed()).subscribe((event: CasinoWindowEvent) => {
            this.onWindowMessage(event);
        });

        this.userService.onUserLogin$.pipe(takeUntilDestroyed()).subscribe(() => this.onLogin());
        this.userService.onUserLogout$.pipe(takeUntilDestroyed()).subscribe(() => this.setMiniGameUrl());

        this.onBetColumnTypeTabSelected(BetColumnType.Betslip);
        this.resetRecentLaunchGame();
    }

    ngOnInit(): void {
        this.dispatcher
            .on<BetColumnType>('BET_COLUMNTYPE_TABSELECTED')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((state) => this.onBetColumnTypeTabSelected(state));
        if (this.casinoMiniGamesConfig.isKYCCheckEnabled) {
            this.kycStatusService.kycStatus.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((status: KycStatus | null) => {
                if (status) {
                    this.isKycVerified = status.kycVerified;
                    this.setMiniGameUrl(this.isKycVerified);
                }
            });
        }
    }

    ngOnDestroy(): void {
        this.resetRecentLaunchGame();
    }

    private onWindowMessage(event: CasinoWindowEvent): void {
        switch (event.constructor) {
            case LobbyLoadedEvent:
                this.hideLoader();
                if (!this.casinoMiniGamesConfig.appendSessionKey) {
                    this.sendSessionKeyUsingPostMessage();
                }
                break;
            case RecentCasinoGameOpenEvent:
                this.miniGamesService.startKeepAlive();
                this.miniGamesService.recentCasinoGameOpen = (event as RecentCasinoGameOpenEvent).gamename;
                this.miniGamesTrackingService.trackMiniGames(this.currentType);
                this.minigamesOpen = this.isGameOpened();
                break;
            case RecentCasinoGameCloseEvent:
                this.balanceService.refresh();
                this.miniGamesService.resetOpenGame();
                this.minigamesOpen = this.isGameOpened();
                break;
            case ShowLoginEvent:
                this.shouldLogin = true;
                this.miniGamesService.recentCasinoGameOpen = (event as ShowLoginEvent).gamename;
                if (!this.userService.isAuthenticated) {
                    this.redirectHelper.goToLogin({ appendReferrer: true });
                }
                break;
            case PingStatusGameOpenEvent:
                this.miniGamesService.startKeepAlive();
                break;
            case WindowScrollStartEvent:
                //TODO any index issue, probably a defect, please investigate
                //@ts-ignore below line doesn't make any sense, but I didn't want to modify any behavior, although this looks like a bug
                this.scrollContainer.scrollToDelta(undefined, (event as WindowScrollStartEvent).data['data'].deltaY);
                break;
        }
    }

    private resetRecentLaunchGame(): void {
        if (!this.shouldLogin) {
            this.miniGamesService.resetOpenGame();
        }
        this.shouldLogin = false;
    }

    private showLoader(): void {
        this.loaderVisible = true;
    }

    private isGameOpened(): boolean {
        return this.userService.isAuthenticated && this.miniGamesService.recentCasinoGameOpen !== '';
    }

    private onLogin(): void {
        this.showLoader();
        this.minigamesUrl = '';
        this.geolocationCheckService.checkGeolocation(() => this.setMiniGameUrl(this.isKycVerified));
    }

    private hideLoader(): void {
        this.loaderVisible = false;
    }

    private setMiniGameUrl(isKycVerified?: boolean): void {
        this.hideLoader();
        this.minigamesUrl = this.miniGamesService.getUrl(isKycVerified);
    }

    private onBetColumnTypeTabSelected(state: BetColumnType): void {
        this.currentType = state;
    }

    private sendSessionKeyUsingPostMessage(): void {
        const event = new MiniGamesWidgetPostSessionKeyEvent();
        this.miniGamesService.postEventsToCasino(event, this.casinoIframe);
    }
}
